/* eslint-disable no-console */

/**
 * Central classification for all E2EE / screenshot-decryption failures.
 *
 * Historically each Vue component string-matched Virgil's English error
 * messages, and the upstream code threw away the information needed to tell
 * the cases apart (e.g. ImageCache never detected a 403, and the card cache
 * collapsed every lookup failure to `false`, which Virgil then reported as
 * "...Got boolean" and we mislabeled as a "duplicate e2ee card").
 *
 * To keep classification reliable, prefer throwing the typed errors below
 * from the service layer. `classifyE2EEError` understands those typed errors
 * first, then falls back to recognizing known raw Virgil errors by name /
 * status / message, and finally to a generic "unknown" result.
 */

export const E2EE_ERROR_CODES = Object.freeze({
  CREDENTIALS_EXPIRED: 'CREDENTIALS_EXPIRED',
  PASSCODE_REQUIRED: 'PASSCODE_REQUIRED',
  DUPLICATE_CARD: 'DUPLICATE_CARD',
  SENDER_KEY_NOT_FOUND: 'SENDER_KEY_NOT_FOUND',
  OLD_DEVICE_KEY: 'OLD_DEVICE_KEY',
  NC1_UNLOCK_REQUIRED: 'NC1_UNLOCK_REQUIRED',
  NC1_ACCESS_NOT_GRANTED: 'NC1_ACCESS_NOT_GRANTED',
  UNKNOWN: 'UNKNOWN'
})

/**
 * The user's session / presigned URL has expired. Recoverable by refreshing
 * the page (web) or reopening the app (in-app).
 */
export class E2EECredentialsExpiredError extends Error {
  constructor(message) {
    super(message || 'Your session has expired.')
    this.name = 'E2EECredentialsExpiredError'
    this.e2eeCode = E2EE_ERROR_CODES.CREDENTIALS_EXPIRED
  }
}

/**
 * The viewer has no usable local private key for this account (passcode never
 * entered, or it was evicted from browser storage). Recoverable by entering
 * the E2EE passcode.
 */
export class E2EEPasscodeRequiredError extends Error {
  constructor(accountId) {
    super('E2EE passcode required')
    this.name = 'E2EEPasscodeRequiredError'
    this.e2eeCode = E2EE_ERROR_CODES.PASSCODE_REQUIRED
    this.accountId = accountId
  }
}

/**
 * The sending device's Virgil card could not be resolved to a single card.
 * `cause` preserves the original Virgil error so it can be classified further
 * (genuine duplicate card vs. not-found vs. transient/auth failure).
 */
/**
 * NC1 (libsodium) record, but this browser's keystore holds no unwrapped
 * identity for the account. Recoverable by a passkey unlock, linking this
 * browser from another device, or the recovery code.
 */
export class Nc1UnlockRequiredError extends Error {
  constructor(accountId) {
    super('NC1 unlock required')
    this.name = 'Nc1UnlockRequiredError'
    this.e2eeCode = E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED
    this.accountId = accountId
  }
}

/**
 * NC1: this browser holds the LOGGED-IN user's own identity, but the viewed
 * account has sealed no key envelope to it — the owner invited them as a
 * recipient but hasn't granted encrypted access yet (or has withdrawn it).
 * Not recoverable on this side; the owner has to grant from Email
 * Recipients. Distinct from "unlock required" (no identity at all) and from
 * a genuine decrypt failure (E2EE campaign finding ⑤c, §5.2).
 */
export class Nc1AccessNotGrantedError extends Error {
  constructor(accountId, viewerEmail, { staleGrant = false } = {}) {
    super('NC1 encrypted access not granted')
    this.name = 'Nc1AccessNotGrantedError'
    this.e2eeCode = E2EE_ERROR_CODES.NC1_ACCESS_NOT_GRANTED
    this.accountId = accountId
    this.viewerEmail = viewerEmail
    // Finding ⑯: the owner DID grant, but to keys this browser no longer
    // holds (the viewer reset/rotated their identity) — needs re-approval.
    this.staleGrant = staleGrant
  }
}

export class E2EESenderCardError extends Error {
  constructor(message, cause) {
    super(message || 'Unable to load the encryption key for this device.')
    this.name = 'E2EESenderCardError'
    this.cause = cause
  }
}

function reopenAction(inApp) {
  return inApp ? 'close and reopen the app' : 'refresh the page'
}

function passcodeRequiredResult(accountId) {
  let message = accountId
    ? `Enter ${accountId}'s legacy encryption (E2EE) passcode to view this.`
    : 'Enter the legacy encryption (E2EE) passcode to view this.'
  return {
    code: E2EE_ERROR_CODES.PASSCODE_REQUIRED,
    title: 'Legacy passcode required',
    message,
    accountId
  }
}

/**
 * Map any error thrown while downloading / decrypting a screenshot to a stable
 * code plus user-facing copy.
 *
 * @param {*} error - the thrown error (typed E2EE error or raw Virgil error)
 * @param {{appDomain?: string, inApp?: boolean}} [opts]
 * @returns {{code: string, title: string, message: string, accountId?: string, rawMessage?: string}}
 */
export function classifyE2EEError(error, opts = {}) {
  const appDomain = opts.appDomain || ''
  const inApp = !!opts.inApp
  const reopen = reopenAction(inApp)

  const name = error && error.name
  const status = error && error.status
  const message = (error && error.message) || ''
  // Typed errors set e2eeCode directly; unwrap a wrapped cause when present.
  const typedCode = error && error.e2eeCode
  const cause = error && error.cause

  // 1. Our own typed errors take precedence.
  if (typedCode === E2EE_ERROR_CODES.CREDENTIALS_EXPIRED) {
    return {
      code: E2EE_ERROR_CODES.CREDENTIALS_EXPIRED,
      title: 'Session expired',
      message: `Your session has expired. Please ${reopen} to continue viewing.`
    }
  }
  if (typedCode === E2EE_ERROR_CODES.PASSCODE_REQUIRED) {
    return passcodeRequiredResult((error && error.accountId) || opts.accountId)
  }
  if (typedCode === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) {
    return {
      code: E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED,
      title: 'Unlock needed',
      message: 'This browser does not hold the encryption key for this account yet. ' +
        'Unlock with your passkey, approve this browser from another device (Link a device), ' +
        'or enter your recovery code.',
      accountId: (error && error.accountId) || opts.accountId
    }
  }
  if (typedCode === E2EE_ERROR_CODES.NC1_ACCESS_NOT_GRANTED) {
    const owner = (error && error.accountId) || opts.accountId
    if (error && error.staleGrant) {
      // Finding ⑯: granted, but to the viewer's PREVIOUS keys.
      return {
        code: E2EE_ERROR_CODES.NC1_ACCESS_NOT_GRANTED,
        title: 'Encrypted access needs re-approval',
        message: 'Your encryption identity changed (it was reset or rotated), so the encrypted access ' +
          (owner ? `${owner} granted` : 'the account owner granted') +
          ' no longer fits your keys. Ask them to open Email Recipients, verify your new safety number, ' +
          'and re-approve your address; nothing on your side is wrong.',
        accountId: owner
      }
    }
    return {
      code: E2EE_ERROR_CODES.NC1_ACCESS_NOT_GRANTED,
      title: 'Encrypted access not granted yet',
      message: (owner ? `${owner} hasn't` : 'The account owner hasn\'t') +
        ' granted you access to their encrypted screenshots and events yet. ' +
        'Ask them to open Email Recipients on their account and grant encrypted access to your address; ' +
        'nothing on your side is wrong.',
      accountId: owner
    }
  }

  // If the sender card couldn't be resolved, classify by the underlying cause.
  if (cause) {
    const causeResult = classifyE2EEError(cause, opts)
    if (causeResult.code !== E2EE_ERROR_CODES.UNKNOWN) {
      return causeResult
    }
  }

  // 2. Known Virgil errors, by name / status.
  if (name === 'UsersFoundWithMultipleCardsError') {
    return {
      code: E2EE_ERROR_CODES.DUPLICATE_CARD,
      title: 'Encryption setup issue',
      message: `There's a problem with this device's encryption setup. Please ${reopen}.` +
        (appDomain ? ` If it persists, email support@${appDomain} and mention code "dupekey".` : '')
    }
  }
  if (status === 'SIGNER_NOT_FOUND' || name === 'UsersNotFoundError') {
    return {
      code: E2EE_ERROR_CODES.SENDER_KEY_NOT_FOUND,
      title: 'Encryption key not found',
      message: 'The encryption key for this device was not found. Please restart the monitored device so it can re-register.'
    }
  }
  if (name === 'PrivateKeyNotFoundError') {
    return passcodeRequiredResult(opts.accountId)
  }

  // 3. Known Virgil errors, by message string (legacy fallbacks).
  if (message === 'Recipient defined with id is not found within message info during data decryption.') {
    return {
      code: E2EE_ERROR_CODES.OLD_DEVICE_KEY,
      title: 'Old legacy encryption key',
      message: 'This image was encrypted with an old encryption key and can no longer be decrypted. After resetting the legacy E2EE passcode, only newly captured data will be viewable. If you haven\'t already, please restart the monitored device so it can recognize the new E2EE passcode.'
    }
  }
  if (message === 'This identity is not registered') {
    return passcodeRequiredResult(opts.accountId)
  }
  if (/^Your credentials have expired/i.test(message)) {
    return {
      code: E2EE_ERROR_CODES.CREDENTIALS_EXPIRED,
      title: 'Session expired',
      message: `Your session has expired. Please ${reopen} to continue viewing.`
    }
  }
  // Defensive: the old "false card" path. With the upstream fixes this should
  // no longer fire, but if it does it almost always means a transient session
  // problem rather than a genuine duplicate card.
  if (message === 'Could not get public key from the second argument.Expected a Virgil Card or a Public Key object. Got boolean') {
    return {
      code: E2EE_ERROR_CODES.UNKNOWN,
      title: 'Decryption error',
      message: `We couldn't decrypt this right now. Please ${reopen}.` +
        (appDomain ? ` If it persists, email support@${appDomain}.` : '')
    }
  }

  // 4. Unknown.
  return {
    code: E2EE_ERROR_CODES.UNKNOWN,
    title: 'Decryption error',
    message: `We couldn't decrypt this. Please ${reopen}.` +
      (appDomain ? ` If it persists, email support@${appDomain}.` : ''),
    rawMessage: message
  }
}

/**
 * Friendly message for failures when the user submits their E2EE passcode in
 * the recovery dialog (restoring the private key). These are different from the
 * decryption failures above — they come from Virgil's restorePrivateKey path —
 * so they get their own mapping. Never returns the raw Virgil error text.
 *
 * @param {*} error
 * @param {{appDomain?: string}} [opts]
 * @returns {string}
 */
export function passcodeEntryErrorMessage(error, opts = {}) {
  const appDomain = opts.appDomain || ''
  const name = error && error.name
  const message = (error && error.message) || ''
  const contact = appDomain ? ` If this keeps happening, contact support@${appDomain}.` : ''

  // Wrong passcode — by far the most common case.
  if (name === 'WrongKeyknoxPasswordError' || /invalid|wrong password/i.test(message)) {
    return 'That passcode is incorrect. Please double-check it and try again.'
  }

  // No backup exists for this account (passcode was never set, or it was reset).
  if (name === 'PrivateKeyNoBackupError' || name === 'RegisterRequiredError' ||
      /backup copy of private key|not registered/i.test(message)) {
    return `No saved passcode was found for this account. The account owner may need to set or reset the legacy E2EE passcode.${contact}`
  }

  // Backup is corrupted / tampered.
  if (name === 'IntegrityCheckFailedError') {
    return `We couldn't verify the saved passcode data for this account. The account owner may need to reset the legacy E2EE passcode.${contact}`
  }

  // Too many attempts / rate limited.
  if ((error && error.status === 429) || /too many requests|rate ?limit/i.test(message)) {
    return 'Too many attempts. Please wait a minute and try again.'
  }

  // Network / connectivity.
  if (name === 'VirgilHttpError' || /network|failed to fetch|timed? ?out|connection/i.test(message)) {
    return 'We couldn\'t reach the server. Please check your connection and try again.'
  }

  // Our own already-friendly thrown message (e.g. key changed mid-restore).
  if (/please try again/i.test(message)) {
    return message
  }

  // Fallback — never surface the raw Virgil error.
  return `We couldn't unlock with that passcode. Please try again.${contact}`
}
